import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { AlertTriangle, CheckCircle2, Clock3, CreditCard, RefreshCw, Send, ShieldAlert } from "lucide-react";
import { CallAPIGetPromise, CallAPIPostPromise } from "Common/commaon";
import { Link } from "react-router-dom";

type CardAlert = {
  alert_id?: number | null;
  alert_priority?: string;
  alert_status?: string;
  brand?: string;
  category: string;
  company_name?: string;
  customer_name: string;
  customer_reminder_count?: number;
  days_remaining: number;
  expiry_date: string;
  last4?: string;
  last_customer_reminder_at?: string | null;
  monthly_amount?: string;
  source: string;
  source_card_id: string;
  subscription_plan?: string;
  user_email?: string;
  user_id?: number;
};

type PaymentAlert = {
  amount?: string | number;
  company_name?: string;
  created_at?: string;
  failure_reason?: string;
  gateway: string;
  priority?: string;
  status: string;
  transaction_id: string;
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
};

type WidgetResponse = {
  categories: Record<string, CardAlert[]>;
  total: number;
};

const CATEGORY_ORDER = [
  "Expiring in 30 Days",
  "Expiring in 14 Days",
  "Expiring in 7 Days",
  "Expiring in 1 Day",
  "Expired",
];

const CRON_ROWS = [
  {
    name: "Card Expiry Reminder Scan",
    schedule: "Daily at 9:00 AM server time",
    coverage: "30, 14, 7, 1 day reminders and expired-card escalation",
    status: "Active",
  },
  {
    name: "Stripe Payment Webhook Alerts",
    schedule: "Realtime",
    coverage: "Payment success, failed invoices, card decline alerts",
    status: "Active",
  },
  {
    name: "Deluxe Payment Webhook Alerts",
    schedule: "Realtime",
    coverage: "Approved and declined Deluxe payments",
    status: "Active",
  },
];

function formatDate(value?: string | null) {
  if (!value) return "Not sent yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function statusBadge(status = "") {
  if (status === "failed") return "danger";
  if (status === "succeeded") return "success";
  if (status === "open") return "warning";
  if (status === "resolved") return "success";
  return "secondary";
}

function categoryBadge(category: string, priority?: string) {
  if (priority === "high" || category === "Expired") return "danger";
  if (category === "Expiring in 1 Day") return "warning";
  if (category === "Expiring in 7 Days") return "info";
  return "secondary";
}

export default function AdminBillingDashboard() {
  const [cardsData, setCardsData] = useState<WidgetResponse>({ categories: {}, total: 0 });
  const [paymentAlerts, setPaymentAlerts] = useState<PaymentAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const cardRows = useMemo(
    () => CATEGORY_ORDER.flatMap((category) => cardsData.categories?.[category] || []),
    [cardsData.categories]
  );

  const paymentSummary = useMemo(() => {
    return paymentAlerts.reduce(
      (acc, alert) => {
        acc.total += 1;
        if (alert.status === "failed") acc.failed += 1;
        if (alert.status === "succeeded") acc.succeeded += 1;
        if (alert.priority === "high") acc.highPriority += 1;
        return acc;
      },
      { total: 0, failed: 0, succeeded: 0, highPriority: 0 }
    );
  }, [paymentAlerts]);

  const loadDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const [cardResponse, paymentResponse]: any = await Promise.all([
        CallAPIGetPromise("/v2/api/billing-alerts/cards-expiring-soon"),
        CallAPIGetPromise("/v2/api/billing-alerts/payment-alerts?limit=10"),
      ]);
      setCardsData(cardResponse?.data || { categories: {}, total: 0 });
      setPaymentAlerts(paymentResponse?.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Unable to load admin billing dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();

  }, []);

  const runScan = async () => {
    setWorking("scan");
    setError("");
    setSuccess("");
    try {
      await CallAPIPostPromise("/v2/api/billing-alerts/scan-card-expiry", { force: false });
      setSuccess("Card expiry cron scan completed.");
      await loadDashboard();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Unable to run card expiry scan.");
    } finally {
      setWorking("");
    }
  };

  const resendReminder = async (alertId?: number | null) => {
    if (!alertId) return runScan();
    setWorking(`resend-${alertId}`);
    setError("");
    setSuccess("");
    try {
      await CallAPIPostPromise(`/v2/api/billing-alerts/cards/${alertId}/resend`, {});
      setSuccess("Reminder resent to customer and admin.");
      await loadDashboard();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Unable to resend reminder.");
    } finally {
      setWorking("");
    }
  };

  const resolveAlert = async (alertId?: number | null) => {
    if (!alertId) return runScan();
    setWorking(`resolve-${alertId}`);
    setError("");
    setSuccess("");
    try {
      await CallAPIPostPromise(`/v2/api/billing-alerts/cards/${alertId}/status`, { status: "resolved" });
      setSuccess("Card alert marked resolved.");
      await loadDashboard();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Unable to resolve alert.");
    } finally {
      setWorking("");
    }
  };

  return (
    <>
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-1">Admin Billing Operations</h4>
          <div className="text-muted">Live card expiry, payment decline, and billing cron status dashboard.</div>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button variant="light" onClick={loadDashboard} disabled={loading}>
            <RefreshCw size={16} className="me-1" /> Refresh
          </Button>
          <Button variant="primary" onClick={runScan} disabled={Boolean(working)}>
            {working === "scan" ? <Spinner animation="border" size="sm" className="me-2" /> : <Send size={16} className="me-1" />}
            Run Card Scan
          </Button>
        </Col>
      </Row>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      <Row className="g-3 mb-3">
        <Col xl={3} md={6}>
          <Card className="h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="avatar-sm rounded bg-warning-subtle text-warning d-flex align-items-center justify-content-center">
                <CreditCard size={20} />
              </div>
              <div>
                <div className="text-muted small">Cards In Expiry Window</div>
                <div className="fs-3 fw-semibold">{cardsData.total || 0}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} md={6}>
          <Card className="h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="avatar-sm rounded bg-danger-subtle text-danger d-flex align-items-center justify-content-center">
                <ShieldAlert size={20} />
              </div>
              <div>
                <div className="text-muted small">High Priority Alerts</div>
                <div className="fs-3 fw-semibold">{(cardsData.categories?.Expired || []).length + paymentSummary.highPriority}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} md={6}>
          <Card className="h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="avatar-sm rounded bg-danger-subtle text-danger d-flex align-items-center justify-content-center">
                <AlertTriangle size={20} />
              </div>
              <div>
                <div className="text-muted small">Payment Declines</div>
                <div className="fs-3 fw-semibold">{paymentSummary.failed}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} md={6}>
          <Card className="h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="avatar-sm rounded bg-success-subtle text-success d-flex align-items-center justify-content-center">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <div className="text-muted small">Successful Payments</div>
                <div className="fs-3 fw-semibold">{paymentSummary.succeeded}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        {CATEGORY_ORDER.map((category) => (
          <Col key={category} xl md={4} sm={6}>
            <Card className="h-100">
              <Card.Body>
                <div className="text-muted small">{category}</div>
                <div className="fs-4 fw-semibold">{cardsData.categories?.[category]?.length || 0}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="g-3">
        <Col xl={8}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">Cards Expiring Soon</h5>
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="secondary-subtle" text="secondary">Last 10 of {cardRows.length}</Badge>
                  <Button as={Link as any} to="/billing/cards-expiring-soon" size="sm" variant="outline-primary">
                    See All
                  </Button>
                </div>
              </div>
              {loading ? (
                <div className="py-5 text-center"><Spinner animation="border" /></div>
              ) : cardRows.length === 0 ? (
                <Alert variant="success" className="mb-0">No saved cards are currently in the expiry window.</Alert>
              ) : (
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Customer</th>
                        <th>Card</th>
                        <th>Plan</th>
                        <th>Reminder</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cardRows.slice(0, 10).map((row) => (
                        <tr key={`${row.source}-${row.source_card_id}`}>
                          <td>
                            <Badge bg={categoryBadge(row.category, row.alert_priority)}>{row.category}</Badge>
                            <div className="small text-muted mt-1">{row.days_remaining} day(s)</div>
                          </td>
                          <td>
                            <div className="fw-semibold">{row.customer_name}</div>
                            <div className="small text-muted">{row.user_email || "N/A"}</div>
                            <div className="small text-muted">{row.company_name || "N/A"}</div>
                          </td>
                          <td>
                            <div>{row.brand || "Card"} ending {row.last4 || "N/A"}</div>
                            <div className="small text-muted">{row.expiry_date}</div>
                          </td>
                          <td>
                            <div>{row.subscription_plan || "N/A"}</div>
                            <div className="small text-muted">${row.monthly_amount || "0.00"}/mo</div>
                          </td>
                          <td>
                            <div>{formatDate(row.last_customer_reminder_at)}</div>
                            <div className="small text-muted">{row.customer_reminder_count || 0} sent</div>
                          </td>
                          <td>
                            <div className="d-flex justify-content-end gap-2">
                              <Button size="sm" variant="outline-primary" onClick={() => resendReminder(row.alert_id)} disabled={Boolean(working)}>
                                Resend
                              </Button>
                              <Button size="sm" variant="outline-success" onClick={() => resolveAlert(row.alert_id)} disabled={Boolean(working)}>
                                Resolve
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xl={4}>
          <Card className="h-100">
            <Card.Body>
              <h5 className="mb-3">Billing Crons & Alert Jobs</h5>
              <div className="vstack gap-3">
                {CRON_ROWS.map((job) => (
                  <div key={job.name} className="border rounded p-3">
                    <div className="d-flex justify-content-between gap-2">
                      <div className="fw-semibold">{job.name}</div>
                      <Badge bg="success">{job.status}</Badge>
                    </div>
                    <div className="small text-muted mt-2 d-flex align-items-center gap-1">
                      <Clock3 size={14} /> {job.schedule}
                    </div>
                    <div className="small text-muted mt-2">{job.coverage}</div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mt-3">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="mb-0">Payment Related Report</h5>
            <div className="d-flex align-items-center gap-2">
              <Badge bg="light" text="dark">Last 10</Badge>
              <Button as={Link as any} to="/billing/payment-alerts" size="sm" variant="outline-primary">
                See All
              </Button>
            </div>
          </div>
          {loading ? (
            <div className="py-5 text-center"><Spinner animation="border" /></div>
          ) : paymentAlerts.length === 0 ? (
            <Alert variant="light" className="mb-0">No payment alerts recorded yet.</Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Customer</th>
                    <th>Company</th>
                    <th>Gateway</th>
                    <th>Amount</th>
                    <th>Transaction</th>
                    <th>Failure Reason</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentAlerts.map((alert) => {
                    const customer = [alert.user_first_name, alert.user_last_name].filter(Boolean).join(" ") || "N/A";
                    return (
                      <tr key={`${alert.gateway}-${alert.transaction_id}-${alert.status}`}>
                        <td>
                          <Badge bg={statusBadge(alert.status)}>{alert.status}</Badge>
                          {alert.priority === "high" ? <div className="small text-danger mt-1">High Priority</div> : null}
                        </td>
                        <td>
                          <div className="fw-semibold">{customer}</div>
                          <div className="small text-muted">{alert.user_email || "N/A"}</div>
                        </td>
                        <td>{alert.company_name || "N/A"}</td>
                        <td>{alert.gateway}</td>
                        <td>${Number(alert.amount || 0).toFixed(2)}</td>
                        <td className="text-break">{alert.transaction_id}</td>
                        <td>{alert.failure_reason || "N/A"}</td>
                        <td>{formatDate(alert.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
}
